import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { EntitiesStructure } from 'common/types/Page'
import { DiagnosticFinding, DiagnosticRule } from '../types'

const RULE_ID = 'master-block'

interface MasterBlockMember {
  id: string
  type: EntityTypeEnum
}

interface MasterBlockGroup {
  roots: MasterBlockMember[]
  members: MasterBlockMember[]
}

function getOrCreateGroup(
  groups: Map<number, MasterBlockGroup>,
  masterBlockId: number,
): MasterBlockGroup {
  const existing = groups.get(masterBlockId)
  if (existing) {
    return existing
  }

  const group: MasterBlockGroup = { roots: [], members: [] }
  groups.set(masterBlockId, group)
  return group
}

export const masterBlockRule: DiagnosticRule = {
  id: RULE_ID,
  name: 'Master Block Issues',
  run: (entities: EntitiesStructure): DiagnosticFinding[] => {
    const findings: DiagnosticFinding[] = []
    const groups = new Map<number, MasterBlockGroup>()

    for (const entity of Object.values(entities)) {
      const masterBlockId =
        'masterBlockId' in entity ? (entity.masterBlockId as number) : undefined
      const isMasterBlockRoot =
        'isMasterBlockRoot' in entity
          ? (entity.isMasterBlockRoot as boolean)
          : false

      if (isMasterBlockRoot && !masterBlockId) {
        findings.push({
          ruleId: RULE_ID,
          severity: 'high',
          entityId: entity.id,
          entityType: entity.type as EntityTypeEnum,
          message: `${entity.type} is marked as master block root but has no masterBlockId`,
        })
        continue
      }

      if (!masterBlockId) {
        continue
      }

      const group = getOrCreateGroup(groups, masterBlockId)
      const member: MasterBlockMember = {
        id: entity.id,
        type: entity.type as EntityTypeEnum,
      }

      group.members.push(member)

      if (isMasterBlockRoot) {
        group.roots.push(member)
      }
    }

    for (const [masterBlockId, group] of groups) {
      if (group.roots.length > 1) {
        for (const root of group.roots) {
          findings.push({
            ruleId: RULE_ID,
            severity: 'high',
            entityId: root.id,
            entityType: root.type,
            message: `Multiple master block roots found for masterBlockId ${masterBlockId}`,
            metadata: { masterBlockId, rootCount: group.roots.length },
          })
        }
      } else if (group.roots.length === 0) {
        const firstMember = group.members[0]
        findings.push({
          ruleId: RULE_ID,
          severity: 'high',
          entityId: firstMember.id,
          entityType: firstMember.type,
          message: `Entities with masterBlockId ${masterBlockId} have no master block root`,
          metadata: { masterBlockId, memberCount: group.members.length },
        })
      } else {
        findings.push({
          ruleId: RULE_ID,
          severity: 'info',
          entityId: group.roots[0].id,
          entityType: group.roots[0].type,
          message: `Master block ${masterBlockId} (${group.members.length} entities)`,
          metadata: { masterBlockId, memberCount: group.members.length },
        })
      }
    }

    return findings
  },
}
